// SPDX-License-Identifier: AGPL-3.0-or-later

import type {CompanionRecording} from '@app/features/companion/commands/CompanionCommands';
import * as CompanionCommands from '@app/features/companion/commands/CompanionCommands';
import styles from '@app/features/companion/components/records/CompanionRecords.module.css';
import {AudioPlayer} from '@app/features/voice/components/media_player/components/AudioPlayer';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {useCallback, useEffect, useRef, useState} from 'react';

const LOAD_RECORDINGS_FAILED_DESCRIPTOR = msg({
	message: 'Failed to load recordings',
	comment: 'Error shown when companion recording audio cannot be loaded for playback.',
});

interface CompanionRecordingPlayerProps {
	readonly sessionId: string;
	readonly recording: CompanionRecording;
}

export function CompanionRecordingPlayer({sessionId, recording}: CompanionRecordingPlayerProps) {
	const {i18n} = useLingui();
	const requestTokenRef = useRef(0);
	const audioUrlRef = useRef<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	const resolveAudio = useCallback((): Promise<string> => {
		const cachedUrl = audioUrlRef.current;
		if (cachedUrl) return Promise.resolve(cachedUrl);
		const requestToken = ++requestTokenRef.current;
		setError(null);
		return CompanionCommands.getRecordingAudio(sessionId, recording.id).then(
			(blob) => {
				if (requestToken !== requestTokenRef.current) throw new Error('Recording load superseded');
				const url = URL.createObjectURL(blob);
				audioUrlRef.current = url;
				return url;
			},
			(loadError) => {
				if (requestToken === requestTokenRef.current) {
					setError(
						CompanionCommands.getCompanionCommandErrorMessage(loadError) ?? i18n._(LOAD_RECORDINGS_FAILED_DESCRIPTOR),
					);
				}
				throw loadError;
			},
		);
	}, [i18n, recording.id, sessionId]);
	useEffect(
		() => () => {
			requestTokenRef.current += 1;
			if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
		},
		[],
	);
	return (
		<div className={styles.recordingPlayback} data-flx="companion.records.recording-playback.container">
			<AudioPlayer
				resolveSrc={resolveAudio}
				duration={recording.duration_ms / 1000}
				variant="bar"
				className={styles.recordingAudioPlayer}
				data-flx="companion.records.recording-playback.player"
			/>
			{error ? (
				<div
					className={`${styles.errorBanner} ${styles.recordingPlaybackError}`}
					role="alert"
					data-flx="companion.records.recording-playback.error"
				>
					<span data-flx="companion.records.companion-recording-player.span">{error}</span>
				</div>
			) : null}
		</div>
	);
}
